import { Label } from "@/components/ui/label";
import ToneSelector, { getRandomTone } from "@/components/ToneSelector";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { type PropsWithChildren, useRef, useState } from "react";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import {
  generateTweet,
  type GenerateTweetInput,
  tweetQueries,
} from "@/api/twitter";
import type { Persona } from "@/api/personas";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogFooter,
} from "@/components/ui/alert-dialog";
import { LoaderCircle } from "lucide-react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";

export function TweetCreationModal({
  persona,
  children,
}: PropsWithChildren<{ persona: Persona }>) {
  const [open, setOpen] = useState(false);

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {children}
      <DialogContent className="max-w-2xl">
        <TweetCreationModalContent
          persona={persona}
          onClose={() => setOpen(false)}
        />
      </DialogContent>
    </Dialog>
  );
}

const LOADING_MESSAGES = [
  "Loading complete agent framework and historical content performance...",
  "Processing engagement patterns and audience response signatures...",
  "Generating unique philosophical perspective and worldview analysis...",
  "Initializing collaborative AI research agent network for trend analysis...",
  "Research agents crawling and cross-validating live news sources...",
  "Synthesizing AI intelligence report with factual verification...",
  "Integrating AI research data into dynamic knowledge base...",
  "Combining refined knowledge base and research insights...",
  "Generating authentic content through autonomous agent collaboration...",
  "Optimizing via RL feedback loop and behavioral reward models...",
  "Finalizing tweet composition with multi-agent validation...",
];

export function TweetCreationModalContent({
  persona,
  onClose,
}: {
  persona: Persona;
  onClose: () => void;
}) {
  const personaId = persona.persona_id;
  const { username } = useAuth();
  const [dataSource, setDataSource] = useState<"knowledge" | "news">(
    "knowledge",
  );
  const [subject, setSubject] = useState("");
  const [tone, setTone] = useState(getRandomTone().description);
  const [maxResults, setMaxResults] = useState(3);
  const [showLoadingModal, setShowLoadingModal] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const [currentLoadingStep, setCurrentLoadingStep] = useState(0);
  const loadingIntervalRef = useRef<any>(null);

  const generateTweetMutation = useMutation({
    mutationFn: (input: GenerateTweetInput) => generateTweet(input),
    onMutate: () => {
      setShowLoadingModal(true);
      setCurrentLoadingStep(0);
      loadingIntervalRef.current = setInterval(() => {
        setCurrentLoadingStep((step) => {
          const newStep = (step + 1) % LOADING_MESSAGES.length;

          if (newStep >= LOADING_MESSAGES.length) {
            clearInterval(loadingIntervalRef.current);
          }

          return newStep;
        });
      }, 5000);
    },
    onSettled: () => stopLoadingMessages(),
    onSuccess: () => {
      toast({ description: "Content successfully created." });
      queryClient.invalidateQueries(
        tweetQueries.latestGeneratedContent(personaId),
      );
      onClose();
    },
    onError: (error: any) => {
      // Check if the error response is available and includes a status code
      if (error.response) {
        const status = error.response.status; // HTTP status code
        const errorMessage = error.response.data.error; // Error message from backend

        if (status === 429) {
          const tier = error.response.data["Access Tier"] || "Unknown";

          toast({
            title: "Rate Limit Reached",
            description: `You've reached the daily limit for the ${tier} tier.`,
            variant: "destructive",
          });
        } else if (
          status === 400 &&
          error.response?.data?.detail?.includes("Insufficient credits")
        ) {
          // Handle insufficient credits error
          toast({
            title: "Insufficient Credits",
            description: `Your agent doesn't have enough credits to perform this operation. Please add more credits to your account.`,
            variant: "destructive",
            action: (
              <Button
                size="sm"
                variant="outline"
                className="bg-background"
                onClick={() => navigate("/account#credits")}
              >
                Add Credits
              </Button>
            ),
          });
        } else {
          handleError(
            `Unexpected Error (${status}): ${errorMessage || "Please try again later."}`,
          );
        }
      } else if (error.request) {
        // No response received
        handleError(
          "No response from the server. Please check your network connection.",
        );
      } else {
        // Other errors
        handleError("An unexpected error occurred: " + error.message);
      }
    },
  });

  const handleError = (message: string) => {
    const container = document.createElement("div");
    container.innerHTML = message; // Set the raw HTML message

    toast({
      description: (
        <div dangerouslySetInnerHTML={{ __html: container.innerHTML }} />
      ),
    });
  };

  // Function to stop loading messages
  const stopLoadingMessages = () => {
    if (loadingIntervalRef.current) {
      clearInterval(loadingIntervalRef.current);
    }
    setCurrentLoadingStep(0);
    setShowLoadingModal(false);
  };

  const generateTweets = async () => {
    if (!personaId) {
      toast({ description: "Please select an agent." });
      return;
    }

    generateTweetMutation.mutate({
      personaId,
      subject,
      tone,
      maxResults,
      dataSource: dataSource === "knowledge" ? "knowledge" : "all",
      username,
    });
  };

  return (
    <>
      <AlertDialog open={showLoadingModal}>
        <AlertDialogContent className="max-w-md">
          <div className="flex flex-col items-center">
            <LoaderCircle className="h-12 w-12 animate-spin stroke-accent m-auto" />
            <p className="mt-4 text-center">
              {LOADING_MESSAGES[currentLoadingStep]}
            </p>
            <AlertDialogFooter className="ml-auto mt-10">
              <AlertDialogCancel onClick={() => stopLoadingMessages()}>
                Process in Background
              </AlertDialogCancel>
            </AlertDialogFooter>
          </div>
        </AlertDialogContent>
      </AlertDialog>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
        <div className="flex flex-wrap items-center gap-2">
          <Label
            htmlFor="tone-selector"
            className="flex-shrink-0 text-sm whitespace-nowrap mr-2"
          >
            Tone
          </Label>
          <div className="flex-grow min-w-0">
            <ToneSelector tone={tone} onToneChange={setTone} />
          </div>
        </div>

        <div className="flex items-center justify-start sm:justify-end gap-2">
          <Label
            htmlFor="tweet-count"
            className="flex-shrink-0 text-sm whitespace-nowrap"
          >
            # of Tweets
          </Label>
          <div className="flex-shrink-0">
            <Select
              value={maxResults.toString()}
              onValueChange={(value) => setMaxResults(Number(value))}
            >
              <SelectTrigger id="tweet-count" className="w-16 h-10 px-2">
                <SelectValue placeholder="3" />
              </SelectTrigger>
              <SelectContent>
                {[1, 2, 3, 4, 5].map((num) => (
                  <SelectItem key={num} value={num.toString()}>
                    {num}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
      </div>

      <div className="flex flex-wrap w-full gap-2 items-center mb-4">
        <Label className="flex-shrink-0 text-sm whitespace-nowrap">
          Topic <span className="text-muted-foreground">(optional)</span>
        </Label>

        <Input
          className="flex-grow min-w-0"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          placeholder="...or enter topic to tweet about"
          aria-label="...or enter topic to tweet about"
        />
      </div>

      <div className="flex flex-col gap-2 mb-4">
        <Label className="flex-shrink-0 text-sm whitespace-nowrap">
          Data Source
        </Label>

        <RadioGroup
          className="flex flex-row flex-wrap gap-x-8 gap-y-4"
          orientation="horizontal"
          onValueChange={(value) =>
            setDataSource(value as "knowledge" | "news")
          }
          value={dataSource}
        >
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="knowledge" id="r1" />
            <Label htmlFor="r1">Knowledge</Label>
          </div>
          <div className="flex items-center space-x-2">
            <RadioGroupItem value="news" id="r2" />
            <Label htmlFor="r2">News</Label>
          </div>
        </RadioGroup>
      </div>

      <Button
        size="sm"
        onClick={generateTweets}
        disabled={generateTweetMutation.isPending}
      >
        Generate tweets {subject ? "from topic" : "& topic"}
      </Button>
    </>
  );
}
